/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Category logic module.
 * Handles category data retrieval for the platform.
 * @module logic/category
 */

import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { refreshToken, signOut } from "../../utils/common.js";

/**
 * Fetches all active categories from the API.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Array>} Array of active category objects or empty array on error
 */
export const getActiveCategories = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}category/active`,
      options,
    );
    const result = await response.json();

    logger.info("active categories result: ", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getActiveCategories(navigate);
    }
    return result || [];
  } catch (error) {
    logger.error("Error fetching active categories:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches a specific category by its ID.
 * @async
 * @param {string} categoryId - Category identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Category object or null on error
 */
export const getCategoryById = async (categoryId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}category/${categoryId}`,
      options,
    );
    const result = await response.json();

    logger.info("category by id result: ", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getCategoryById(categoryId, navigate);
    }
    return result || null;
  } catch (error) {
    logger.error("Error fetching category by ID:", {
      err: sanitizeError(error),
    });
    return null;
  }
};
